import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { PublicSharedProject, Endpoint } from '@mockd/shared';
import { getApiBaseUrl, getMockApiSubdomainUrl } from '../config';
import { Alert, CopyButton } from '../components/common';
import { Skeleton } from '../components/Skeleton';

export function ShareView() {
  const { token } = useParams<{ token: string }>();
  const [project, setProject] = useState<PublicSharedProject | null>(null);
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Endpoints fail separately from the project itself: the header and mock URL
  // are still useful, so this renders in place of the list rather than
  // replacing the whole page.
  const [endpointsError, setEndpointsError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      setEndpointsError(null);

      try {
        // Resolve token to project
        const shareRes = await fetch(`${getApiBaseUrl()}/api/share/${token}`);
        if (!shareRes.ok) {
          if (shareRes.status === 404) {
            setError('This share link is invalid or has been revoked.');
          } else {
            setError('Failed to load shared project.');
          }
          return;
        }

        const shareJson = await shareRes.json();
        const projectData = shareJson.data.project as PublicSharedProject;
        setProject(projectData);

        // Load endpoints via the share token — the recipient has no session, so
        // the authenticated project route would 404 for every owned project.
        try {
          const endpointsRes = await fetch(`${getApiBaseUrl()}/api/share/${token}/endpoints`);
          if (!endpointsRes.ok) throw new Error(`HTTP ${endpointsRes.status}`);
          const endpointsJson = await endpointsRes.json();
          setEndpoints(endpointsJson.data || []);
        } catch {
          setEndpointsError('Failed to load endpoints for this project.');
        }
      } catch {
        setError('Failed to load shared project.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token]);

  if (loading) {
    return <Skeleton variant="share-view" />;
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-error mb-4">{error || 'Project not found'}</h1>
          <Link to="/" className="link link-primary">Go to Home</Link>
        </div>
      </div>
    );
  }

  const mockUrl = getMockApiSubdomainUrl(project.subdomain);

  return (
    <div className="min-h-screen bg-base-200">
      <Alert variant="info" className="rounded-none">
        Read-only view &mdash; <Link to="/register" className="link font-medium">Sign up</Link> to create your own mock API
      </Alert>

      <header className="bg-base-100 border-b border-base-300">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <h1 className="text-base-content">{project.name}</h1>
          <p className="text-sm text-base-content/80">
            <code className="text-xs bg-base-200 px-1.5 py-0.5 rounded-field">{project.subdomain}</code>
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="card bg-base-100 mb-6 p-4">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <label className="text-sm text-base-content/80 block mb-1">Mock API URL</label>
              <code className="text-sm font-mono text-base-content block truncate">{mockUrl}</code>
            </div>
            <CopyButton text={mockUrl} label="Copy URL" iconOnly className="shrink-0" />
          </div>
        </div>

        <h2 className="text-base-content mb-4">
          Endpoints{endpointsError ? '' : ` (${endpoints.length})`}
        </h2>

        {endpointsError ? (
          <Alert variant="error">{endpointsError}</Alert>
        ) : endpoints.length === 0 ? (
          <div className="text-center py-12 text-base-content/80">
            No endpoints configured yet.
          </div>
        ) : (
          <div className="space-y-2">
            {endpoints.map((endpoint) => (
              <div key={endpoint.id} className="card bg-base-100 p-4">
                <div className="flex items-center gap-3">
                  <span className="badge badge-sm badge-outline font-mono">
                    {endpoint.statusCode}
                  </span>
                  <code className="text-sm font-mono text-base-content">{endpoint.path}</code>
                  <span className="text-xs text-base-content/70 ml-auto">{endpoint.contentType}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
